import { createApi } from '@reduxjs/toolkit/query/react';
import Helpers from '../../lib/helpers';
import { baseQueryWithReauth } from './baseQuery';

export const categoryServices = createApi({
  reducerPath: 'categoryServices',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Categories'],

  endpoints: (builder) => ({
    getCategories: builder.query({
      query: ({ page, search }) => ({
        url: `/v1/speciality-operator/full-category`,
        method: 'GET',
        params: {
          page,
          search,
        },
      }),
      providesTags: ['Categories'],
    }),

    addFullCategory: builder.mutation({
      query: ({ body }) => ({
        url: `/v1/speciality-operator/full-category`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Categories'],
    }),

    editCategory: builder.mutation({
      query: ({ body }) => ({
        url: `/v1/speciality-operator/category`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Categories'],
    }),

    addSpeciality: builder.mutation({
      query: ({ body, method = 'POST' }) => ({
        url: `/v1/speciality-operator/speciality`,
        method,
        body,
      }),
      async onQueryStarted(_, mutationLifeCycleApi) {
        try {
          await mutationLifeCycleApi.queryFulfilled;
          mutationLifeCycleApi.dispatch(
            categoryServices.util.invalidateTags(['Categories'])
          );
        } catch {}
      },
    }),

    addSkill: builder.mutation({
      query: ({ body, method = 'POST' }) => ({
        url: `/v1/speciality-operator/skill`,
        method,
        body,
      }),
      async onQueryStarted(_, mutationLifeCycleApi) {
        try {
          await mutationLifeCycleApi.queryFulfilled;
          mutationLifeCycleApi.dispatch(
            categoryServices.util.invalidateTags(['Categories'])
          );
        } catch {}
      },
    }),

    uploadDocs: builder.mutation({
      query: ({ files }) => ({
        url: `/v1/categories-documents`,
        headers: {
          'ACCEPT-CONTENT-TYPE': 'multipart/form-data',
        },
        method: 'POST',
        body: files,
      }),
    }),

    getCategoriesReport: builder.query<void, { params?: { search?: string } }>({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const result = await fetchWithBQ({
          url: '/v1/speciality-operator/full-category/export-excel',
          method: 'GET',
          params: _arg?.params,
          responseHandler: (response) => response.blob(),
        });

        if (result.error) return { error: result.error };

        const blob = result.data as Blob;

        Helpers.downloadBlobFile({ blob, fileName: 'report.xlsx' });

        return { data: undefined };
      },
    }),

    importTemplate: builder.mutation({
      query: ({ files }) => ({
        url: `/v1/speciality-operator/import-excel`,
        headers: {
          'ACCEPT-CONTENT-TYPE': 'multipart/form-data',
        },
        method: 'POST',
        body: files,
      }),
      invalidatesTags: ['Categories'],
    }),
  }),
});

export const {
  useAddFullCategoryMutation,
  useUploadDocsMutation,
  useGetCategoriesQuery,
  useAddSpecialityMutation,
  useEditCategoryMutation,
  useAddSkillMutation,
  useLazyGetCategoriesReportQuery,
  useImportTemplateMutation,
} = categoryServices;
